import { useEffect, useRef } from 'react'

import { UseFormReturn } from 'react-hook-form'

import { Default, DefaultValuesPayload } from '@/Data/defaultValues'

/**
 * Prefills a React Hook Form with backend-provided defaults when enabled.
 * Keeps user-edited (dirty) fields and refreshes non-dirty fields on reopen.
 */
export function useDefaultsPrefill({
  enabled,
  payload,
  form,
}: {
  enabled: boolean
  payload: DefaultValuesPayload | undefined
  form: UseFormReturn
}) {
  const formRef = useRef(form)

  // Guards against out-of-order async responses overwriting newer defaults.
  const requestIdRef = useRef(0)
  let payloadKey = ''
  try {
    payloadKey = payload ? JSON.stringify(payload) : ''
  } catch {
    payloadKey = ''
  }

  useEffect(() => {
    if (!enabled) return
    if (!payload?.entityName) return

    let cancelled = false
    const requestId = ++requestIdRef.current

    Default.create(payload)
      .then((defaults) => {
        if (cancelled) return
        if (requestId !== requestIdRef.current) return

        const currentForm = formRef.current
        const currentValues = currentForm.getValues() as Record<string, unknown>
        const dirtyFields = currentForm.formState.dirtyFields as
          | Record<string, unknown>
          | undefined

        const nextValues: Record<string, unknown> = { ...defaults }

        for (const [key, isDirty] of Object.entries(dirtyFields ?? {})) {
          if (!isDirty) continue
          nextValues[key] = currentValues[key]
        }

        // `keepDirtyValues` preserves the dirty state of fields the user has
        // already edited. Callers rely on `dirtyFields` to tell user input
        // apart from prefilled defaults when building an update payload.
        currentForm.reset(nextValues, { keepDirtyValues: true })
      })
      .catch(() => {
        // Defaults are an enhancement; if they fail, keep the form usable.
      })

    return () => {
      cancelled = true
    }
    // payloadKey is a stable serialized proxy for payload
    // using payload directly would re-run on every render when callers pass a new object reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, payloadKey])
}
